import type { TaxAssessment } from "../../api/client";
import { fmtCurrency, fmtSF } from "../../utils/format";

interface TaxAssessmentCardProps {
  assessment: TaxAssessment;
}

export default function TaxAssessmentCard({ assessment: a }: TaxAssessmentCardProps) {
  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm overflow-hidden">
      <div className="bg-grove-50 px-6 py-3 border-b border-grove-200 flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-bark-700">
            Assessment Data
          </span>
          <span className="text-xs text-bark-400 ml-3">
            Folio: {a.folio_number}
          </span>
        </div>
        {a.source && (
          <span className="text-[10px] text-bark-400 uppercase tracking-wider">
            {a.source === "fdor_statewide" ? "FDOR Statewide" : a.source}
          </span>
        )}
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Valuation */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-400 mb-3">
            Valuation
          </h4>
          <ValRow label="Just (Market) Value" value={a.just_value} />
          <ValRow label="Assessed Value (NSD)" value={a.assessed_value_nsd} />
          <ValRow label="Taxable Value (NSD)" value={a.taxable_value_nsd} />
          <ValRow label="Land Value" value={a.land_value} />
          <ValRow label="Building Value" value={a.building_value} />
          <ValRow
            label="Homestead Value"
            value={a.homestead_value}
            note={a.has_exemptions ? "Exemptions applied" : undefined}
          />
        </div>

        {/* Property details */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-400 mb-3">
            Property Details
          </h4>
          <TextRow label="Owner" value={a.owner_name} />
          <TextRow
            label="Address"
            value={
              [a.physical_address, a.physical_city, a.physical_zip]
                .filter(Boolean)
                .join(", ") || null
            }
          />
          <TextRow
            label="Year Built"
            value={a.year_built?.toString() || null}
          />
          <TextRow
            label="Building Area"
            value={
              a.building_area_sf
                ? fmtSF(a.building_area_sf)
                : null
            }
          />
          <TextRow
            label="Land Area"
            value={
              a.land_area_sf
                ? fmtSF(a.land_area_sf)
                : null
            }
          />
          <TextRow label="DOR Use Code" value={a.use_code} />
        </div>
      </div>
    </div>
  );
}

function ValRow({
  label,
  value,
  note,
}: {
  label: string;
  value: number | null;
  note?: string;
}) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-bark-500">{label}</span>
      <span className="text-bark-800 font-medium tabular-nums">
        {value != null ? fmtCurrency(value) : "--"}
        {note && (
          <span className="ml-2 text-xs text-honey-600">({note})</span>
        )}
      </span>
    </div>
  );
}

function TextRow({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-bark-500">{label}</span>
      <span className="text-bark-800 font-medium">{value || "--"}</span>
    </div>
  );
}
